import { useState, useRef, useEffect } from "react";
import { Search, Briefcase, Sparkles, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { useDebounce } from "../hooks/useDebounce";
import { SalaryData } from "../../../types/SalaryData";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { ValuePreviewPanel } from "./value-preview-panel";
import { postJobtitles } from "../../../utils/api";
import { SalaryCheckRequestModel } from "../../../types/SalaryCheckRequest";
import Analytics from "../../../utils/analytics";
import { educationLevels, regions, ageRanges, sectors, responsibilities, situations } from "../data/form-options";

interface MobileWizardProps {
  salaryData: SalaryData;
  onDataChange: (data: Partial<SalaryData>) => void;
  onCalculate: (data: SalaryCheckRequestModel) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  resetTrigger?: number;
  contentSourceId: number;
}

export interface JobTitleItem {
  id: number;
  title: string;
}

// Steps: 0=Intro, 1=Situation, 2=JobTitle, 3=Background, 4=Salary+Context
const TOTAL_INPUT_STEPS = 4; // not counting intro

export function MobileWizard({ salaryData, onDataChange, onCalculate, isCollapsed, onToggleCollapse, resetTrigger, contentSourceId }: MobileWizardProps) {
  const [currentStep, setCurrentStep] = useState(0); // 0 = intro
  const [direction, setDirection] = useState(1);
  const [jobTitleInput, setJobTitleInput] = useState(salaryData.jobTitle || "");
  const [showJobTitles, setShowJobTitles] = useState(false);
  const [jobTitleResults, setJobTitleResults] = useState<JobTitleItem[]>([]);
  const [jobTitleError, setJobTitleError] = useState<string | null>(null);
  const [educationId, setEducationId] = useState<number | null>(null);
  const [regionId, setRegionId] = useState<number | null>(null);
  const [responsebilityId, setResponsebilityId] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const MIN_SEARCH_LENGTH = 3;
  const SEARCH_DELAY = 300;

  const salaryResultRequest = () => {
    //When editing result educationId has to be found again
    const matchingEducationId = () => {
      if(educationId == null && salaryData.education != null && salaryData.education !== "") {
        return educationLevels.find(e => e.title === salaryData.education)?.id ?? 0
      } else {
        return educationId;
      }  
    } 

     const matchingSituationId = () => {
      return salaryData.situationId || (situations.find(e => e.title === salaryData.situation)?.id ?? 0);
    } 

    //request data
    const requestData: SalaryCheckRequestModel = {
      name: "",
      email: "",
      contentSourceId: contentSourceId,
      situationId: salaryData.situationId || matchingSituationId(),
      jobTitleId: salaryData.jobTitleId === 0 ? (jobTitleResults.find(j => j.title === salaryData.jobTitle)?.id ?? 0) : salaryData.jobTitleId,
      jobTitle: salaryData.jobTitle,
      salary: salaryData.salary,
      educationId: matchingEducationId(),
      jobExperience: salaryData.experience,
      regionId: regionId,
      age: null,
      ageGroup: salaryData.age,
      jobSector: salaryData.sector,
      managementExperience: responsebilityId,
      cookie: "",
      educationLevel: salaryData.education
    };
    console.log("data i  mobil", requestData)
    onCalculate(requestData);    
  }

  const searchJobTitles = async (query: string) => {
    try {
      const response = await postJobtitles({ query: query });
      const data = Array.isArray(response.data)
        ? response.data.map((item: any, idx: number) => ({
            id: item.id ?? idx,
            title: item.title ?? String(item),
          }))
        : [];
      setJobTitleResults(data);
      setShowJobTitles(true);
    } catch (err) {
      setJobTitleError("Kunne ikke hente jobtitler. Prøv igen.");
    }
  };

  const debouncedSearch = useDebounce((query: string) => {
    if (query.length >= MIN_SEARCH_LENGTH) {
      searchJobTitles(query);
    } else if (query.length === 0) {
      setJobTitleResults([]);
      setShowJobTitles(false);
    }
  }, SEARCH_DELAY);

 /*  useEffect(() => {
    setJobTitleInput(salaryData.jobTitle || "");
  }, [salaryData.jobTitle]); */

  useEffect(() => {
    if (resetTrigger !== undefined && resetTrigger > 0) {
      setCurrentStep(1);
      setDirection(1);
    }
  }, [resetTrigger]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current && !inputRef.current.contains(event.target as Node) &&
        dropdownRef.current && !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowJobTitles(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

/*   const filteredJobTitles = jobTitles.filter((t) =>
    t.toLowerCase().includes(jobTitleInput.toLowerCase())
  ); */

  const isIntro = currentStep === 0;
  const inputStep = currentStep - 1; // 0-based input step index
  const progressPct = isIntro ? 0 : (inputStep + 1) / TOTAL_INPUT_STEPS * 100;

  const canAdvance = () => {
    if (isIntro) return true;
    switch (inputStep) {
      case 0: return !!salaryData.situation; // Step 1: Situation (påkrævet)
      case 1: return !!salaryData.jobTitle; // Step 2: Jobtitel (påkrævet)
      case 2: return true; // Step 3: Baggrund (valgfrit)
      case 3: return salaryData.salary > 0; // Step 4: Bruttoløn påkrævet
      default: return false;
    }
  };

  const handleNext = () => {
    track();

    setDirection(1);
    if (currentStep < TOTAL_INPUT_STEPS) {
      setCurrentStep(currentStep + 1);
    } else {
      salaryResultRequest();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setDirection(-1);
      setCurrentStep(currentStep - 1);
    }
  };

  const handleJobTitleSelect = (title: string, id: number) => {
    setJobTitleInput(title);
    onDataChange({ jobTitle: title, jobTitleId: id });
    setShowJobTitles(false);
  };

  //tracking
  const track = () => {
    let situationMap =
    salaryData.situation === "Er min løn fair" ? "_fair_salary" :
    salaryData.situation === "Lønforhandling" ? "_negotiation" :
    salaryData.situation === "Nyuddannet" ? "_graduated" :
    salaryData.situation === "Søger nyt job" ? "_new_job" :
    "";

    let background = ""
    if(salaryData.experience) {
      background += `_${salaryData.experience}`
    }
    if(salaryData.education) {
      background += `_${salaryData.education}`
    }
    if(salaryData.age) {
      background += `_${salaryData.age}`
    }

    let resultContentType = "";
    if (salaryData.region) {
      resultContentType += `_${salaryData.region}`
    } 
    if(salaryData.sector) {
      resultContentType += `_${salaryData.sector}`
    }
    if(salaryData.experience) {
      resultContentType += `_${salaryData.experience}`
    }

    if (currentStep === 0) {
      Analytics.getInstance().trackSalaryCheck("situation_shown")
    } else if (currentStep === 1) {
      Analytics.getInstance().trackSalaryCheck(`situation${situationMap}`)
    } else if (currentStep === 2) {
      Analytics.getInstance().trackSalaryCheck(`title_${salaryData.jobTitle}`)
    } else if (currentStep === 3) {
      Analytics.getInstance().trackSalaryCheck(`background${background}`)
    } else if (currentStep === 4) {
      const contentType = `result${resultContentType}`
      Analytics.getInstance().trackSalaryCheckWithValue(contentType, `${salaryData.salary}`)
    }
  }


  // Collapsed summary
  if (isCollapsed) {
    return (
      <div className="bg-white rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.04)] border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 space-y-2">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-wide text-[#7b7b7a]">Jobtitel</span>
            <div className="text-sm font-bold text-[#3A3A39]">{salaryData.jobTitle}</div>
          </div>
          <div className="flex flex-wrap gap-1.5 pt-1">
            {salaryData.experience !== null && (
              <span className="px-2.5 py-1 rounded-full text-[10px] font-medium bg-[#e6f4f5] text-[#008e9f]">{salaryData.experience} års erfaring</span>
            )}
            {salaryData.region && (
              <span className="px-2.5 py-1 rounded-full text-[10px] font-medium bg-[#e6f4f5] text-[#008e9f]">{salaryData.region}</span>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.04)] border border-gray-100 ${showJobTitles ? "overflow-visible" : "overflow-hidden"}`}>
      {/* Progress bar - hidden on intro */}
      {!isIntro && (
        <div className="px-5 pt-5 pb-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold uppercase tracking-wide text-[#7b7b7a]">
              Trin {inputStep + 1} af {TOTAL_INPUT_STEPS}
            </span>
          </div>
          <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#D76F2C] rounded-full transition-all duration-500"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      )}

      {/* Step content */}
      <div className={`px-5 pb-5 ${showJobTitles ? "overflow-visible" : "overflow-hidden"} ${isIntro ? "pt-6" : "pt-4"}`}>
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentStep}
            custom={direction}
            initial={{ x: direction * 60, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: direction * -60, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
          >
            {/* Step 0: Intro */}
            {isIntro && (
              <div className="space-y-5">
                <div className="text-center">
                  <h2 className="text-[22px] font-bold text-[#3A3A39] tracking-[-0.5px] mb-2">
                    Er din løn fair?
                  </h2>
                  <p className="text-sm text-[#7b7b7a] max-w-[300px] mx-auto mb-4">
                    Gennemfør 4 trin og se, hvordan du ligger i markedet.
                  </p>

                  {/* Start button - placed right after intro text */}
                  <button
                    onClick={handleNext}
                    className="button w-full flex items-center justify-center gap-2 py-3.5 rounded-[40px] text-base font-bold text-white bg-[#D76F2C] hover:bg-[#c15f1f] active:bg-[#a94f18] transition-all active:scale-[0.98] shadow-sm min-h-[44px] cursor-pointer"
                  >
                    Start beregning
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>

                {/* Value preview panels - now below the button */}
                <ValuePreviewPanel />
              </div>
            )}

            {/* Step 1: Situation */}
            {currentStep === 1 && (
              <div className="space-y-3">
                <div>
                  <h3 className="text-[18px] font-bold text-[#3A3A39] mb-1">Hvad er din situation? *</h3>
                  <p className="text-xs text-[#7b7b7a]">Din situation påvirker din løn</p>
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                  {situations.map((s) => (
                    <button
                      key={s.title}
                      onClick={() => { onDataChange({ situation: s.title, situationId: s.id }); }}
                      className={`text-center px-4 py-4 rounded-[20px!important] text-[14px] font-medium transition-all cursor-pointer ${
                        salaryData.situation === s.title
                          ? "bg-[#008E9F!important] text-[#fff!important]"
                          : "bg-[#f0f5f5!important] text-[#3A3A39!important] active:bg-[#e6eeee!important]"
                      }`}
                    >
                      {s.title}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Job Title */}
            {currentStep === 2 && (
              <div className="space-y-3">
                <div>
                  <h3 className="text-[18px] font-bold text-[#3A3A39] mb-1">Hvad er din jobtitel? *</h3>
                  <p className="text-xs text-[#7b7b7a]">Vi finder løndata der matcher din stilling</p>
                </div>
                <div className="relative">
                  <div className={`relative flex items-center rounded-lg transition-all ${
                    salaryData.jobTitle
                      ? "border-2 border-[#479376] bg-[#edf4f1]"
                      : "border border-gray-300 bg-white"
                  } focus-within:border-[#479376] focus-within:border-2`}>
                    <Search className="absolute left-3.5 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input
                      ref={inputRef}
                      type="text"
                      value={jobTitleInput}
                      onChange={(e) => { setJobTitleInput(e.target.value); debouncedSearch(e.target.value); }}
                      onFocus={(e) => {
                        setShowJobTitles(true);
                        e.target.select(); // Select all text so user can easily search again
                      }}
                      placeholder="Søg fx projektleder, konsulent…"
                      className="w-full pl-10 pr-3 py-3.5 bg-transparent rounded-lg focus:outline-none text-[14px] text-[#3A3A39] placeholder:text-gray-400"
                    />
                  </div>
                   {showJobTitles && (
                    <div
                      ref={dropdownRef}
                      className="absolute z-50 w-full mt-1.5 bg-white rounded-lg shadow-lg border border-gray-200 max-h-52 overflow-y-auto"
                      onMouseDown={(e) => e.preventDefault()}
                      onTouchStart={(e) => e.preventDefault()}
                    >
                      {jobTitleError && (
                        <div className="px-4 py-3 text-[15px] text-red-600">{jobTitleError}</div>
                      )}
                      {!jobTitleError && jobTitleResults.length === 0 && jobTitleInput.trim() !== "" && (
                        <div className="px-4 py-3 text-[15px] text-[#3A3A39]">Ingen jobtitler fundet.</div>
                      )}

                      {jobTitleResults.map((item, i) => (
                        <button
                          key={item.title}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleJobTitleSelect(item.title, item.id);
                          }}
                          onTouchEnd={(e) => {
                            e.preventDefault();
                            handleJobTitleSelect(item.title, item.id);
                          }}
                          className={`w-full text-left px-4 py-3 text-[14px] hover:bg-[#f0f5f5!important] active:bg-[#e6f4f5!important] transition-colors cursor-pointer flex items-center gap-2 ${
                            i === 0 ? "rounded-t-lg" : ""
                          } ${i === jobTitleResults.length - 1 ? "rounded-b-lg" : ""} ${
                            item.title === salaryData.jobTitle ? "bg-[#e6f4f5] text-[#3A3A39] font-medium" : "text-[#3A3A39]"
                          }`}
                          style={{ justifyContent: "flex-start" }}
                        >
                          <Briefcase className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                          {item.title}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 3: Background (Experience, Education, Age) */}
            {currentStep === 3 && (
              <div className="space-y-3">
                <div>
                  <h3 className="text-[18px] font-bold text-[#3A3A39] mb-1">Fortæl om din baggrund</h3>
                  <p className="text-xs text-[#7b7b7a]">Disse oplysninger gør vores vurdering mere præcis (valgfrit)</p>
                </div>
                <div className="space-y-3 pt-1">
                  {/* Experience */}
                  <div>
                    <label className="text-[14px] font-medium text-[#3A3A39] mb-1.5 block">Erfaring</label>
                    <div className="flex items-center gap-3">
                      <div className={`relative flex items-center rounded-lg transition-all flex-1 ${
                        salaryData.experience !== null
                          ? "border-2 border-[#479376] bg-[#edf4f1]"
                          : "border border-gray-300 bg-white"
                      } focus-within:border-[#479376] focus-within:border-2`}>
                        <input
                          type="number"
                          min={0}
                          max={50}
                          value={salaryData.experience ?? ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            onDataChange({ experience: val === "" ? null : Math.min(50, Math.max(0, Number(val))) });
                          }}
                          placeholder="fx 5"
                          className="w-full px-3.5 py-3.5 bg-transparent rounded-lg focus:outline-none text-[14px] text-[#3A3A39] placeholder:text-gray-400"
                        />
                      </div>
                      <span className="text-[14px] text-[#3A3A39] shrink-0 font-medium">år</span>
                    </div>
                  </div>

                  {/* Education */}
                  <RefinementSelect
                    label="Uddannelse"
                    value={salaryData.education}
                    onChange={(v) => {onDataChange({ education: v }); setEducationId(educationLevels.find(e => e.title === v)?.id ?? 0)}}
                    options={educationLevels.map(e => e.title)}
                    placeholder="Vælg uddannelsesniveau"
                  />

                  {/* Age */}
                  <RefinementSelect
                    label="Alder"
                    value={salaryData.age}
                    onChange={(v) => onDataChange({ age: v })}
                    options={ageRanges}
                    placeholder="Vælg aldersgruppe"
                  />
                </div>
              </div>
            )}

            {/* Step 4: Salary and Context (Region, Sector, Responsibility, Salary) */}
            {currentStep === 4 && (
              <div className="space-y-3">
                <div>
                  <h3 className="text-[18px] font-bold text-[#3A3A39] mb-1">Løn og arbejdsforhold</h3>
                  <p className="text-xs text-[#7b7b7a]">Hjælp os med at finde den mest relevante sammenligning</p>
                </div>
                <div className="space-y-3 pt-1">
                  {/* Region */}
                  <RefinementSelect
                    label="Region"
                    value={salaryData.region}
                    onChange={(v) => { onDataChange({ region: v }), setRegionId(regions.find(r => r.title === v)?.id ?? null)}}
                    options={regions.map(r => r.title)}
                    placeholder="Vælg region"
                  />

                  {/* Sector */}
                  <RefinementSelect
                    label="Sektor"
                    value={salaryData.sector}
                    onChange={(v) => onDataChange({ sector: v })}
                    options={sectors}
                    placeholder="Vælg sektor"
                  />

                  {/* Responsibility */}
                  <RefinementSelect
                    label="Ansvarsniveau"
                    value={salaryData.responsibility}
                    onChange={(v) => {onDataChange({ responsibility: v }); setResponsebilityId(responsibilities.findIndex(r => r === v) ?? null)}}
                    options={responsibilities}
                    placeholder="Vælg ansvarsniveau"
                  />

                  {/* Salary */}
                  <div>
                    <label className="text-[14px] font-medium text-[#3A3A39] mb-1.5 block">Bruttoløn (kr./md.) *</label>
                    <div className={`relative flex items-center rounded-lg transition-all ${
                      salaryData.salary > 0
                        ? "border-2 border-[#479376] bg-[#edf4f1]"
                        : "border border-gray-300 bg-white"
                    } focus-within:border-[#479376] focus-within:border-2`}>
                      <span className="absolute left-3.5 text-[14px] text-gray-400 font-medium pointer-events-none">kr.</span>
                      <input
                        type="number"
                        value={salaryData.salary || ""}
                        onChange={(e) => onDataChange({ salary: Number(e.target.value) })}
                        placeholder="fx 35.000"
                        className="w-full pl-10 pr-3 py-3.5 bg-transparent rounded-lg focus:outline-none text-[14px] text-[#3A3A39] placeholder:text-gray-400"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation - hidden on intro since button is inline */}
        {!isIntro && (
          <div className="flex gap-3 mt-6 justify-around">
            {currentStep > 0 && (
              <button
                onClick={handleBack}
                className="text-[15px] font-medium text-[#008E9F!important] underline hover:text-[#006d7a!important] transition-colors min-w-[44px] min-h-[44px] cursor-pointer"
              >
                Tilbage
              </button>
            )}
            <button
              onClick={handleNext}
              disabled={!canAdvance()}
              className={`button ml-auto flex items-center justify-center gap-2 py-3.5 px-8 rounded-[40px] text-base font-bold text-white disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-[0.98] shadow-sm min-h-[44px] cursor-pointer ${
                currentStep === TOTAL_INPUT_STEPS
                  ? "bg-[#D76F2C] hover:bg-[#c15f1f] active:bg-[#a94f18]"
                  : "bg-[#D76F2C] hover:bg-[#c15f1f] active:bg-[#a94f18]"
              }`}
            >
              {currentStep === TOTAL_INPUT_STEPS ? (
                <>

                  Se mit resultat
                </>
              ) : (
                <>
                  Næste
                  <ArrowRight className="w-3.5 h-3.5" />
                </>
              )}
            </button>
          </div>
        )}
        
        {/* Info text - only shown on final step */}
   {/*      {currentStep === TOTAL_INPUT_STEPS && (
          <p className="text-[11px] text-center text-[#7b7b7a] px-5 pt-[12px] pb-3">
            Baseret på flere datakilder for et mere præcist resultat.
          </p>
        )} */}
      </div>
    </div>
  );
}

function IntroValueRow({ icon: Icon, text }: { icon: React.ComponentType<{ className?: string }>; text: string }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="w-7 h-7 rounded-lg bg-[#e6f4f5] flex items-center justify-center shrink-0">
        <Icon className="w-3.5 h-3.5 text-[#008e9f]" />
      </div>
      <span className="text-[13px] font-medium text-[#3A3A39]">{text}</span>
    </div>
  );
}

function IntroExtendedRow({ icon: Icon, text }: { icon: React.ComponentType<{ className?: string }>; text: string }) {
  return (
    <div className="flex items-center gap-2.5">
      <Icon className="w-3.5 h-3.5 text-[#d76f2c]/70 shrink-0" />
      <span className="text-[12px] text-[#7b7b7a]">{text}</span>
    </div>
  );
}

// Phase 2 refinement card - shown after free result on mobile (only if fields are missing)
export function RefinementCard({ salaryData, onDataChange, onRecalculate }: {
  salaryData: SalaryData;
  onDataChange: (data: Partial<SalaryData>) => void;
  onRecalculate: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const hasAny = !!salaryData.education || !!salaryData.age;

  // Don't show if both fields are already filled
  if (salaryData.education && salaryData.age) {
    return null;
  }

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="w-full bg-white rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.04)] border border-gray-100 px-5 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-all"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-[#e6f4f5] flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-[#008e9f]" />
          </div>
          <div className="text-left">
            <div className="text-[13px] font-bold text-[#3A3A39]">Gør vurderingen mere præcis</div>
            <div className="text-[11px] text-[#7b7b7a]">Tilføj uddannelse og alder</div>
          </div>
        </div>
        <ArrowRight className="w-4 h-4 text-[#008e9f]" />
      </button>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.04)] border border-gray-100 overflow-hidden"
    >
      <div className="px-5 py-4">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-[#008e9f]" />
          <h3 className="text-sm font-bold text-[#3A3A39]">Gør din vurdering mere præcis</h3>
        </div>
        <p className="text-xs text-[#7b7b7a] mb-4">Disse felter giver et mere nøjagtigt resultat</p>

        <div className="space-y-3">
          <RefinementSelect
            label="Uddannelse"
            value={salaryData.education}
            onChange={(v) => onDataChange({ education: v })}
            options={educationLevels.map(e => e.title)}
            placeholder="Vælg uddannelsesniveau"
          />
          <RefinementSelect
            label="Alder"
            value={salaryData.age}
            onChange={(v) => onDataChange({ age: v })}
            options={ageRanges}
            placeholder="Vælg aldersgruppe"
          />
        </div>

        <button
          onClick={onRecalculate}
          disabled={!hasAny}
          className="w-full mt-4 py-3 rounded-[40px] text-base font-bold text-white bg-[#D76F2C] hover:bg-[#c15f1f] active:bg-[#a94f18] disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-[0.98] min-h-[44px] cursor-pointer"
        >
          Opdater resultat
        </button>
      </div>
    </motion.div>
  );
}

function RefinementSelect({ label, value, onChange, options, placeholder }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder: string;
}) {
  return (
    <div>
      <label className="text-[14px] font-medium text-[#3A3A39] mb-1.5 block">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          style={{
            width: "100%",
            padding: "0.3rem 1rem",
            borderRadius: "0.5rem",
            fontSize: "15px",
            transition: "all 0.2s ease",
            minHeight: "auto",
            borderStyle: "solid",
            borderWidth: value ? "2px" : "1px",
            borderColor: value ? "#479376" : "var(--color-gray-300)",
            color: value ? "#3A3A39" : "var(--color-gray-400)",
            backgroundColor: value ? "#edf4f1" : "#ffffff",
            fontWeight: value ? 500 : 400,
            boxSizing: "border-box",
            outline: "none",
            justifyContent: "space-between",
          }}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          {options.map((o) => (
            <SelectItem
              key={o}
              value={o}
              className="text-[14px] px-4 py-3 hover:bg-[#f0f5f5] cursor-pointer text-[#3A3A39] data-[state=checked]:bg-[#e6f4f5] data-[state=checked]:text-[#3A3A39] rounded"
            >
              {o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}